/**
 * C7 — is this page an agency's portal host (tailorme.synanalytics.co.za or
 * reports.tailorme.co.za) rather than SynAnalytics itself? If so, the
 * sign-in page wears the agency's name, colour and logo, not ours (white
 * label). Local development can pretend with ?portal_host=<host>.
 */

import { useEffect, useState } from 'react';

import { apiCall } from '../api.ts';

const MAIN_HOSTS = new Set([
  'analytics.synteralabs.co.za',
  'synanalytics-web.pages.dev',
  'localhost',
  '127.0.0.1',
]);
const DEV_KEY = 'synanalytics.portal-host';

export interface PortalBrand {
  name: string;
  accent: string | null;
  logo: string | null;
}

export type PortalState =
  | { kind: 'main' }
  | { kind: 'loading'; host: string }
  | { kind: 'portal'; host: string; brand: PortalBrand }
  | { kind: 'unknown'; host: string };

function devOverride(): string | null {
  if (!import.meta.env.DEV) return null;
  try {
    const asked = new URLSearchParams(window.location.search).get('portal_host');
    if (asked !== null) sessionStorage.setItem(DEV_KEY, asked);
    return sessionStorage.getItem(DEV_KEY) || null;
  } catch {
    return null;
  }
}

/** The portal host this page stands for, or null on SynAnalytics' own sites. */
export function portalHostname(): string | null {
  const override = devOverride();
  if (override) return override;
  const host = window.location.hostname;
  if (MAIN_HOSTS.has(host) || host.endsWith('.synanalytics-web.pages.dev')) return null;
  return host;
}

export function usePortalState(): PortalState {
  const host = portalHostname();
  const [state, setState] = useState<PortalState>(
    host ? { kind: 'loading', host } : { kind: 'main' },
  );
  useEffect(() => {
    if (!host) return;
    apiCall<PortalBrand>(`/public/portal-host?host=${encodeURIComponent(host)}`)
      .then((brand) => {
        setState({ kind: 'portal', host, brand });
        document.title = brand.name;
      })
      .catch(() => setState({ kind: 'unknown', host }));
  }, [host]);
  return state;
}
